<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import { db } from '../db/client';
import { COLOR_HEX } from '../colors';
import type { ItemFull } from '../types';

const props = defineProps<{ slug: string }>();
const router = useRouter();

const item = ref<ItemFull | null>(null);
const loading = ref(true);
const error = ref<string | null>(null);

const panel = useTemplateRef<HTMLElement>('panel');
const scrollEl = useTemplateRef<HTMLElement>('scrollEl');

const TILE_RENDER = 128;
const sprite = computed(() => {
  if (!item.value) return {};
  return {
    backgroundPosition: `-${item.value.sprite_x * TILE_RENDER}px -${item.value.sprite_y * TILE_RENDER}px`,
    backgroundSize: `${32 * TILE_RENDER}px auto`,
    width: `${TILE_RENDER}px`,
    height: `${TILE_RENDER}px`,
  };
});

// ── Свойства: статы + теги ───────────────────────────────────────────────────
const STAT_LABEL: Record<string, string> = {
  damage: 'Урон', tears: 'Слёзы', shotspeed: 'Скор. выстрела', range: 'Дальность',
  tearheight: 'Высота слёз', speed: 'Скорость', size: 'Размер', luck: 'Удача', health: 'Здоровье',
};
const STAT_ORDER = ['damage', 'tears', 'shotspeed', 'range', 'tearheight', 'speed', 'size', 'luck', 'health'];
const CAT_ORDER = ['trigger', 'mechanic', 'effect', 'archetype'];
const CAT_LABEL: Record<string, string> = {
  trigger: 'Триггер', mechanic: 'Механики', effect: 'Эффекты', archetype: 'Архетипы',
};
const CAT_CHIP: Record<string, string> = {
  trigger: 'bg-stone-800 text-stone-300 ring-stone-700',
  mechanic: 'bg-sky-500/10 text-sky-300 ring-sky-500/25',
  effect: 'bg-violet-500/10 text-violet-300 ring-violet-500/25',
  archetype: 'bg-amber-500/10 text-amber-300 ring-amber-500/25',
};
const catClass = (cat: string) => CAT_CHIP[cat] ?? 'bg-bg-card text-stone-300 ring-stone-700';

const sortedStats = computed(() =>
  item.value ? [...item.value.stats].sort((a, b) => STAT_ORDER.indexOf(a.stat) - STAT_ORDER.indexOf(b.stat)) : [],
);
const colorTags = computed(() => item.value?.tags.filter((t) => t.category === 'color') ?? []);
const tagGroups = computed(() =>
  item.value
    ? CAT_ORDER.map((cat) => ({ cat, label: CAT_LABEL[cat], tags: item.value!.tags.filter((t) => t.category === cat) }))
        .filter((g) => g.tags.length)
    : [],
);
const hasParsed = computed(() => sortedStats.value.length > 0 || (item.value?.tags.length ?? 0) > 0);
const fmtFlat = (n: number) => (n > 0 ? '+' : '') + n.toString().replace('.', ',');
const fmtMult = (n: number) => '×' + n.toString().replace('.', ',');

async function load() {
  loading.value = true;
  error.value = null;
  try {
    item.value = await db().one(props.slug);
    if (!item.value) error.value = `Предмет «${props.slug}» не найден`;
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    loading.value = false;
  }
}

onMounted(load);
watch(() => props.slug, () => {
  // reset swipe state и грузим новые данные
  dragY.value = 0;
  load();
  scrollEl.value?.scrollTo(0, 0);
});

function close() {
  if (window.history.length > 1) router.back();
  else router.replace({ name: 'items' });
}

async function share() {
  const url = location.origin + router.resolve({ name: 'item', params: { slug: props.slug } }).href;
  try {
    if (navigator.share) {
      await navigator.share({ title: item.value?.name_ru ?? item.value?.name_en ?? 'Isaac', url });
    } else {
      await navigator.clipboard.writeText(url);
    }
  } catch {
    /* user cancelled */
  }
}

// Перехват клика по ссылкам item:[X] — рендерятся как plain <a href=/item/slug>,
// сюда подвязываем SPA-навигацию.
function onContentClick(e: MouseEvent) {
  const a = (e.target as HTMLElement | null)?.closest('a[data-item-ref]');
  if (!a) return;
  const href = (a as HTMLAnchorElement).getAttribute('href') ?? '';
  if (!href.startsWith('/item/')) return;
  e.preventDefault();
  router.push(href);
}

// ── Swipe-to-dismiss ─────────────────────────────────────────────────────────
// Жест вниз → translateY → если прошли threshold (или быстрый flick) → close.
// На тач-устройствах используем touch-события с preventDefault (это перебивает
// touch-action: pan-y и нативный скролл). На десктопе — pointer-события мыши.
// Pointer capture НЕ используем на тач — иначе он перехватывает тап по кнопкам.
const DISMISS_THRESHOLD = 110;            // пикселей вниз — закрываем
const VELOCITY_THRESHOLD = 0.6;            // px/ms — быстрый swipe тоже закрывает
const dragY = ref(0);                      // текущий translateY (только вниз)
const dragging = ref(false);

// Разрешаем dismiss-drag только когда контент проскроллен в самый верх.
function atScrollTop(): boolean {
  const el = scrollEl.value;
  return !el || el.scrollTop <= 0;
}

function finishDrag(dy: number, velocity: number) {
  dragging.value = false;
  const dismiss = dy > DISMISS_THRESHOLD || (dy > 30 && velocity > VELOCITY_THRESHOLD);
  if (dismiss) {
    const h = panel.value?.getBoundingClientRect().height ?? 600;
    dragY.value = h;
    setTimeout(close, 160);
  } else {
    dragY.value = 0;
  }
}

// ── Touch (мобила) ──
// candidate — жест-кандидат (стартовали в верху скролла); active — подтверждён
// именно dismiss-drag (палец пошёл вниз). prev/last — две последние пробы для
// расчёта скорости flick'а.
let tCandidate = false;
let tActive = false;
let tStartY = 0;
let tStartX = 0;
let tPrevY = 0;
let tPrevT = 0;
let tLastY = 0;
let tLastT = 0;

function onTouchStart(e: TouchEvent) {
  if (e.touches.length !== 1) { tCandidate = false; return; }
  const t = e.touches[0]!;
  tCandidate = atScrollTop();
  tActive = false;
  tStartY = t.clientY;
  tStartX = t.clientX;
  tPrevY = tLastY = t.clientY;
  tPrevT = tLastT = e.timeStamp;
  dragY.value = 0;
}

function onTouchMove(e: TouchEvent) {
  if (!tCandidate || e.touches.length !== 1) return;
  const t = e.touches[0]!;
  const dy = t.clientY - tStartY;
  const dx = t.clientX - tStartX;
  if (!tActive) {
    // Определяем намерение по первому заметному сдвигу.
    if (dy > 6 && Math.abs(dy) > Math.abs(dx)) {
      tActive = true;
      dragging.value = true;
    } else if (dy < -2 || Math.abs(dx) > Math.abs(dy) + 4) {
      // вверх или вбок — это не dismiss, отдаём нативному скроллу
      tCandidate = false;
      return;
    } else {
      return;
    }
  }
  e.preventDefault();              // глушим нативный скролл/overscroll
  dragY.value = Math.max(0, dy);
  tPrevY = tLastY; tPrevT = tLastT;
  tLastY = t.clientY; tLastT = e.timeStamp;
}

function onTouchEnd(e: TouchEvent) {
  if (!tCandidate) return;
  const wasActive = tActive;
  tCandidate = false;
  tActive = false;
  if (!wasActive) return;
  const dt = Math.max(1, tLastT - tPrevT);
  const v = (tLastY - tPrevY) / dt;
  finishDrag(dragY.value, v);
  void e;
}

// ── Pointer (только мышь, десктоп) ──
let mouseDragging = false;
let mStartY = 0;
let mPrevY = 0;
let mPrevT = 0;
let mLastY = 0;
let mLastT = 0;

function onPointerDown(e: PointerEvent) {
  if (e.pointerType !== 'mouse' || e.button !== 0) return;
  if ((e.target as HTMLElement | null)?.closest('button, a')) return; // не крадём клик
  if (!atScrollTop()) return;
  mouseDragging = true;
  dragging.value = true;
  mStartY = e.clientY;
  mPrevY = mLastY = e.clientY;
  mPrevT = mLastT = e.timeStamp;
  dragY.value = 0;
}

function onPointerMove(e: PointerEvent) {
  if (!mouseDragging) return;
  const dy = e.clientY - mStartY;
  dragY.value = Math.max(0, dy);
  mPrevY = mLastY; mPrevT = mLastT;
  mLastY = e.clientY; mLastT = e.timeStamp;
}

function onPointerUp() {
  if (!mouseDragging) return;
  mouseDragging = false;
  const dt = Math.max(1, mLastT - mPrevT);
  const v = (mLastY - mPrevY) / dt;
  finishDrag(dragY.value, v);
}

// Touch-листенеры вешаем вручную: touchmove обязан быть НЕ passive,
// иначе preventDefault не сработает и нативный скролл перебьёт жест.
onMounted(() => {
  const el = panel.value;
  if (!el) return;
  el.addEventListener('touchstart', onTouchStart, { passive: true });
  el.addEventListener('touchmove', onTouchMove, { passive: false });
  el.addEventListener('touchend', onTouchEnd, { passive: true });
  el.addEventListener('touchcancel', onTouchEnd, { passive: true });
});
onBeforeUnmount(() => {
  const el = panel.value;
  if (!el) return;
  el.removeEventListener('touchstart', onTouchStart);
  el.removeEventListener('touchmove', onTouchMove);
  el.removeEventListener('touchend', onTouchEnd);
  el.removeEventListener('touchcancel', onTouchEnd);
});

const panelStyle = computed(() => ({
  transform: dragY.value ? `translateY(${dragY.value}px)` : '',
  transition: dragging.value ? 'none' : 'transform 0.18s ease',
}));

const backdropStyle = computed(() => {
  // фон чуть прозрачнее по мере drag — визуальный feedback
  const alpha = dragY.value ? Math.max(0.2, 0.7 - dragY.value / 700) : 0.7;
  return { background: `rgba(0,0,0,${alpha})` };
});
</script>

<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-50" :style="backdropStyle" @click="close" />
    <div
      ref="panel"
      class="fixed inset-x-0 bottom-0 top-12 z-50 flex flex-col rounded-t-2xl bg-bg-elev pb-safe shadow-2xl touch-pan-y sm:inset-y-12 sm:inset-x-12 sm:rounded-2xl"
      :style="panelStyle"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
    >
      <!-- drag-handle: визуальный hint для swipe -->
      <div class="flex justify-center pt-2 pb-1">
        <div class="h-1 w-10 rounded-full bg-stone-700" />
      </div>

      <div class="sticky top-0 z-10 flex items-center justify-between border-b border-stone-800 bg-bg-elev px-3 py-2">
        <button
          v-if="item"
          class="rounded-md px-3 py-1 text-stone-300 hover:text-stone-100"
          @click.stop="share"
          aria-label="Поделиться"
        >↗ Поделиться</button>
        <span v-else />
        <button
          class="rounded-md px-3 py-1 text-stone-300 hover:text-stone-100"
          @click.stop="close"
          aria-label="Закрыть"
        >Назад ✕</button>
      </div>

      <div ref="scrollEl" class="flex-1 overflow-y-auto overscroll-contain p-4">
        <div v-if="loading" class="py-8 text-center text-stone-500">Загрузка…</div>
        <div v-else-if="error" class="py-8 text-center text-red-400">{{ error }}</div>
        <div v-else-if="item" class="space-y-4" @click="onContentClick">
          <div class="flex items-start gap-4">
            <div class="sprite-atlas shrink-0 rounded-md" :style="sprite" />
            <div class="min-w-0 flex-1">
              <h1 class="text-xl font-semibold leading-tight" data-testid="modal-title">{{ item.name_ru ?? item.name_en }}</h1>
              <p v-if="item.name_ru" class="text-sm text-stone-400">{{ item.name_en }}</p>
              <p v-if="item.ingame_text" class="mt-2 text-sm italic text-stone-300">"{{ item.ingame_text }}"</p>
              <div class="mt-3 flex flex-wrap gap-2 text-xs text-stone-400">
                <span v-if="item.quality !== null" class="rounded bg-bg-card px-2 py-0.5">Качество {{ item.quality }}</span>
                <span v-if="item.type" class="rounded bg-bg-card px-2 py-0.5">{{ item.type }}</span>
                <span v-if="item.charges" class="rounded bg-bg-card px-2 py-0.5">
                  {{ item.charges }} {{ item.charge_type === 'timed' ? 'сек' : 'зарядов' }}
                </span>
              </div>
            </div>
          </div>

          <section v-if="hasParsed" class="space-y-3">
            <!-- Статы -->
            <div v-if="sortedStats.length" class="flex flex-wrap gap-2">
              <div
                v-for="s in sortedStats" :key="s.stat"
                class="flex items-center gap-2 rounded-lg bg-bg-card px-3 py-1.5"
              >
                <span class="text-xs font-medium text-stone-400">{{ STAT_LABEL[s.stat] ?? s.stat }}</span>
                <span class="flex items-baseline gap-1.5 text-sm font-bold tabular-nums">
                  <span v-if="s.flat != null" :class="s.flat > 0 ? 'text-emerald-400' : 'text-rose-400'">{{ fmtFlat(s.flat) }}</span>
                  <span v-if="s.mult != null" :class="s.mult >= 1 ? 'text-emerald-400' : 'text-rose-400'">{{ fmtMult(s.mult) }}</span>
                </span>
              </div>
            </div>

            <!-- Цвет + теги — каждая категория своей строкой -->
            <dl class="space-y-2">
              <div v-if="colorTags.length" class="flex gap-2">
                <dt class="w-20 shrink-0 pt-1 text-xs text-stone-500">Цвет</dt>
                <dd class="flex flex-1 flex-wrap gap-1.5">
                  <span
                    v-for="t in colorTags" :key="t.slug"
                    class="inline-flex items-center gap-1.5 rounded-full bg-bg-card px-2.5 py-1 text-xs text-stone-200"
                  >
                    <span class="h-3 w-3 rounded-full ring-1 ring-white/15" :style="{ background: COLOR_HEX[t.slug] ?? '#888' }" />
                    {{ t.name }}
                  </span>
                </dd>
              </div>
              <div v-for="g in tagGroups" :key="g.cat" class="flex gap-2">
                <dt class="w-20 shrink-0 pt-1 text-xs text-stone-500">{{ g.label }}</dt>
                <dd class="flex flex-1 flex-wrap gap-1.5">
                  <span
                    v-for="t in g.tags" :key="t.slug"
                    class="rounded-full px-2.5 py-1 text-xs ring-1"
                    :class="catClass(g.cat)"
                  >{{ t.name }}</span>
                </dd>
              </div>
            </dl>
          </section>

          <section v-if="item.description_ru">
            <h2 class="mb-1 text-xs uppercase tracking-wide text-stone-500">Описание</h2>
            <div class="prose prose-invert prose-sm max-w-none text-stone-200" v-html="item.description_ru" />
          </section>

          <section v-if="item.pools.length">
            <h2 class="mb-2 text-xs uppercase tracking-wide text-stone-500">Где найти</h2>
            <div class="flex flex-wrap gap-1.5">
              <span v-for="p in item.pools" :key="p.slug" class="rounded bg-bg-card px-2 py-0.5 text-xs text-stone-200">
                {{ p.name_ru }}
              </span>
            </div>
          </section>

          <section v-if="item.unlock">
            <h2 class="mb-1 text-xs uppercase tracking-wide text-stone-500">Открытие</h2>
            <pre class="whitespace-pre-wrap break-words text-sm text-stone-300">{{ item.unlock }}</pre>
          </section>

          <section v-for="n in item.notes" :key="n.kind + n.body.slice(0, 16)">
            <h2 class="mb-1 text-xs uppercase tracking-wide text-stone-500">
              {{ n.kind === 'synergy' ? 'Синергии' : n.kind === 'bug' ? 'Баги' : n.kind }}
            </h2>
            <div class="prose prose-invert prose-sm max-w-none text-stone-200" v-html="n.body" />
          </section>

          <footer class="border-t border-stone-800 pt-3 text-xs text-stone-500">
            <a v-if="item.source_dg_url" :href="item.source_dg_url" target="_blank" rel="noopener" class="underline">
              dead-god.ru ↗
            </a>
            <a v-if="item.source_ii_url" :href="item.source_ii_url" target="_blank" rel="noopener" class="ml-3 underline">
              isaac-items.ru ↗
            </a>
          </footer>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* Стилизация для item:[X] ссылок, отрисованных через v-html в worker'е */
:deep(.ref-link) {
  color: theme('colors.amber.300');
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}
:deep(.ref-link:hover) {
  color: theme('colors.amber.200');
}
:deep(.ref-unknown) {
  color: theme('colors.stone.500');
  font-style: italic;
}
</style>
